import { motion, AnimatePresence } from 'framer-motion';
import React, { useEffect, useMemo } from 'react';
import { observer, useComputed } from '@legendapp/state/react';
import { logger } from '@/lib/utils/logger';
import { adviceState, adviceActions } from '@/lib/store/advice.store';
import { sidebarState } from '@/lib/store/sidebar.store';
import { ConversationListItem } from '@/types';
import { ChatItem } from '@/components/features/chat/components/sidebar/ChatItem';
import { useI18n } from '@/locales/client';
import { useToast } from '@/lib/hooks/useToast';
import { Session } from 'next-auth';

// Define Framer Motion variants for the group container.
const groupVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      when: 'beforeChildren',
      staggerChildren: 0.05,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 30,
      when: 'afterChildren',
      staggerChildren: 0.03,
    },
  },
};

interface ChatGroupProps {
  title: string;
  chats: ConversationListItem[];
  pathname: string;
  currentChat: string | null;
  currentChatId: string | null;
  setCurrentChat: (id: string | null) => void;
  setCurrentChatValue: (value: string | null) => void;
  handleEdit: (e: any, id: string) => void;
  handleDelete: (id: string) => void;
  currentChatRef: React.RefObject<HTMLInputElement>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  currentChatValue: string | null;
  onChatClick: (id: string) => Promise<void>;
  onNavigate: (path: string) => void;
  isPending: boolean;
  isTransitioning: boolean;
  toast: ReturnType<typeof useToast>['toast'];
  session: Session | null;
  // Pass the conversations observable (so child components can also subscribe if needed)
  conversationsState: typeof adviceState.conversations;
}

export const ChatGroup = observer((props: ChatGroupProps) => {
  const {
    title,
    chats,
    pathname,
    currentChat,
    setCurrentChat,
    setCurrentChatValue,
    handleEdit,
    handleDelete,
    currentChatRef,
    handleInputChange,
    currentChatValue,
    onChatClick,
    onNavigate,
    isPending,
    isTransitioning,
    toast,
    session,
    conversationsState,
  } = props;
  const t = useI18n();

  // Subscribe to some global state for debugging or conditional styling.
  const storeState = useComputed(() => ({
    status: adviceState.status.get(),
    isTransitioning: adviceState.isTransitioning.get(),
    currentChatId: adviceState.currentChatId.get(),
    sidebarStatus: sidebarState.status.get(),
    isDeleting: sidebarState.isDeleting.get(),
  }));

  // Ensure chats is a safe array.
  const currentChats = useMemo(
    () => (Array.isArray(chats) ? chats : []),
    [chats]
  );

  // Create a composite key for the list so that changes force remount if needed.
  const chatListKey = useMemo(
    () => currentChats.map((chat) => `${chat.id}-${chat.title}`).join('|'),
    [currentChats]
  );

  if (!currentChats.length) {
    logger.debug('[ChatGroup] No chats to render for:', { title });
    return null;
  }

  return (
    <motion.div
      variants={groupVariants}
      initial='hidden'
      animate='visible'
      exit='exit'
      layout
      className='w-full'
      data-testid={`chat-group-${title}`}
      data-chat-count={currentChats.length}
      data-is-pending={storeState.get().sidebarStatus === 'pending'}
      data-is-transitioning={storeState.get().isTransitioning}
      data-is-deleting={storeState.get().isDeleting}
    >
      <motion.div
        layout
        className='space-y-2 border-b border-purple-400 dark:border-purple-700 p-2 text-xs text-gray-800 dark:text-gray-100 shadow-sm dark:shadow-none'
      >
        {title}
      </motion.div>
      <AnimatePresence mode='popLayout' initial={false}>
        <div key={chatListKey} className='space-y-[1px]'>
          {currentChats.map((chat) => (
            <ChatItem
              key={chat.id}
              chat={chat}
              currentChat={currentChat}
              onNavigate={onNavigate}
              toast={toast}
            />
          ))}
        </div>
      </AnimatePresence>
    </motion.div>
  );
});
